import {
	CSSProperties,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { ListItemProps, ListProps } from "./types";
import { useStyle } from "./style";
import { Flex, GapSize } from "@/components/atoms/flex";
import { mergeClasses, Text } from "@fluentui/react-components";

/** The size of a row as it was just before it was hovered. */
interface HoveredItemLayout {
	height: number;
	isContentOnOneLine: boolean;
}

const measureItemLayout = (row: HTMLLIElement): HoveredItemLayout => {
	const content = row.querySelector<HTMLElement>("[data-list-item-content]");
	const contentHeight = content?.getBoundingClientRect().height ?? 0;
	const lineHeight = content
		? Number.parseFloat(getComputedStyle(content).lineHeight)
		: Number.NaN;

	return {
		height: row.getBoundingClientRect().height,
		isContentOnOneLine:
			contentHeight > 0 && Math.round(contentHeight / lineHeight) === 1,
	};
};

/**
 * Holds one-line content on its line while the end media takes width from it, cut at the character
 * that runs out of room: line-clamp could only stop at a word boundary and leave a gap before the end
 * media. Longer content needs nothing, as the content class already clamps it to its two lines.
 */
const oneLineContentStyle: CSSProperties = {
	display: "block",
	whiteSpace: "nowrap",
	textOverflow: "ellipsis",
};

/** Carries a row's item key on its element, so the list's hover tracking can tell the rows apart. */
const RowKeyAttribute = "data-list-key";

const rowOf = (target: EventTarget | null) =>
	target instanceof Element
		? target.closest<HTMLLIElement>(`li[${RowKeyAttribute}]`)
		: null;

export const List = (props: ListProps) => {
	const {
		items,
		classesName,
		styles,
		selectedId,
		getEndMedia,
		rowsOutOfTabOrder,
	} = props;
	const classes = useStyle();
	const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
	// Hovering swaps the header media for the end media. The header media can be the taller of the two
	// (the queue column with its tags), and the end media takes width from the content, which would wrap
	// onto another line. Either way the row would change height, slide out from under the cursor and
	// hand the hover to its neighbour. So the hovered row keeps the height it had on entry, and content
	// on one line stays on it and ends in an ellipsis instead.
	const [hoveredItemLayout, setHoveredItemLayout] =
		useState<HoveredItemLayout>();
	const selectedElementRef = useRef<HTMLLIElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	// Read by the hover listeners, which are attached once.
	const itemsRef = useRef(items);
	itemsRef.current = items;

	useEffect(() => {
		if (selectedElementRef.current) {
			selectedElementRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [selectedId]);

	// Hover is tracked on the list rather than on each row: a row is not interactive itself (its
	// button is).
	useEffect(() => {
		const list = listRef.current;

		if (!list) {
			return undefined;
		}

		let hoveredKey: string | null = null;

		const hover = (row: HTMLLIElement | null) => {
			const key = row?.getAttribute(RowKeyAttribute) ?? null;

			if (key === hoveredKey) {
				return;
			}

			if (row) {
				setHoveredItemLayout(measureItemLayout(row));
			}

			hoveredKey = key;
			setHoveredItemKey(key);
		};

		// A row whose call menu is open keeps its end media while the pointer is in the menu.
		const isCallMenuOpen = () =>
			itemsRef.current.some(
				(item) => String(item.key) === hoveredKey && item.isCallMenuOpened
			);

		const onMouseOver = (event: MouseEvent) => {
			const row = rowOf(event.target);

			if (row === null && isCallMenuOpen()) {
				return;
			}

			hover(row);
		};

		const onMouseLeave = () => {
			if (isCallMenuOpen()) {
				return;
			}

			hover(null);
		};

		list.addEventListener("mouseover", onMouseOver);
		list.addEventListener("mouseleave", onMouseLeave);

		return () => {
			list.removeEventListener("mouseover", onMouseOver);
			list.removeEventListener("mouseleave", onMouseLeave);
		};
	}, []);

	const onClickCallback = useCallback((item: ListItemProps) => {
		!!item.onClick && item.onClick(item);
	}, []);

	const selectedClass = useCallback(
		(item: ListItemProps) =>
			selectedId == item.key
				? mergeClasses(classes.selectedItem, classesName?.itemSelected)
				: undefined,
		[selectedId, classesName]
	);

	const selectedMainTextClass = useCallback(
		(item: ListItemProps) =>
			selectedId === item.key
				? classesName?.mainTextSelected
				: classesName?.mainText,
		[selectedId, classesName]
	);

	const selectedIconClass = useCallback(
		(item: ListItemProps) =>
			selectedId == item.key ? classesName?.startMediaSelected : undefined,
		[selectedId, classesName]
	);

	const selectedStyle = useCallback(
		(item: ListItemProps) =>
			selectedId == item.key ? styles?.itemSelected : undefined,
		[selectedId, styles]
	);

	const rowActionTabIndex = rowsOutOfTabOrder ? -1 : undefined;

	const listItemBuilder = useCallback(
		(item: ListItemProps) => {
			const resolvedEndMedia = getEndMedia ? getEndMedia(item) : item.endMedia;
			const isHovered = hoveredItemKey === String(item.key);
			const showEndMedia =
				(item.notePopoverOpenedKeys !== undefined &&
					item.notePopoverOpenedKeys.includes(item.key)) ||
				(isHovered && resolvedEndMedia);
			const hoveredLayout = isHovered ? hoveredItemLayout : undefined;

			const headerText = (
				<Text
					title={typeof item.header == "string" ? item.header : undefined}
					className={mergeClasses(
						classes.textContent,
						classes.mainText,
						classes.mainTextColor,
						item.headerAdornment ? classes.adornedHeaderText : undefined,
						selectedMainTextClass(item)
					)}
					children={item.header}
				/>
			);

			const text = (
				<Flex column className={classes.itemMiddleWrapper}>
					<Flex
						vAlign="center"
						className={classes.contentWrapper}
						gap={GapSize.Medium}
					>
						<Flex column className={classes.itemMiddleWrapper}>
							{item.headerAdornment ? (
								<Flex
									vAlign="center"
									gap={GapSize.Small}
									className={classes.adornedHeader}
								>
									{headerText}
									{item.headerAdornment}
								</Flex>
							) : (
								headerText
							)}
							{item.secondaryHeader && (
								<Text
									title={
										typeof item.header == "string" ? item.header : undefined
									}
									className={mergeClasses(
										classes.textContent,
										classes.mainText,
										classes.mainTextColor,
										selectedMainTextClass(item)
									)}
									children={item.secondaryHeader}
								/>
							)}
							{!!item.subHeader && (
								<span
									className={mergeClasses(
										classes.textContent,
										classes.secondaryText,
										classes.secondaryTextColor
									)}
									title={
										typeof item.subHeader === "string"
											? item.subHeader
											: undefined
									}
								>
									{item.subHeader}
								</span>
							)}
						</Flex>
						{!showEndMedia && (
							<Flex
								gap={GapSize.Medium}
								vAlign="center"
								className={mergeClasses(
									classes.contentMedia,
									classes.secondaryText,
									classes.mainTextColor,
									classesName?.headerMedia
								)}
							>
								{item.headerMedia}
							</Flex>
						)}
					</Flex>
					<Flex
						className={mergeClasses(
							classes.contentWrapper,
							item.content ? classes.contentRow : undefined
						)}
					>
						<span
							data-list-item-content
							title={item.content}
							className={mergeClasses(
								classes.contentText,
								classes.secondaryText,
								classes.secondaryTextColor
							)}
							style={
								hoveredLayout?.isContentOnOneLine
									? oneLineContentStyle
									: undefined
							}
						>
							{item.content}
						</span>
						{!!item.contentMedia && (
							<span className={classes.contentMedia}>{item.contentMedia}</span>
						)}
					</Flex>
				</Flex>
			);

			// A clickable row's action is a button around its text. The start and end media stay
			// outside it: they hold buttons of their own (the icon's popover trigger, Pick up), which
			// must not be nested in another.
			const rowAction = item.onClick ? (
				<button
					type="button"
					tabIndex={rowActionTabIndex}
					className={classes.rowAction}
					onClick={() => onClickCallback(item)}
				>
					{text}
				</button>
			) : (
				text
			);

			return (
				<li
					key={item.key}
					data-list-key={String(item.key)}
					className={mergeClasses(
						classes.itemRoot,
						classesName?.item,
						selectedClass(item),
						classesName?.shadow
					)}
					style={{
						...styles?.item,
						...selectedStyle(item),
						...(hoveredLayout && { minHeight: hoveredLayout.height }),
					}}
					ref={item.key === selectedId ? selectedElementRef : null}
				>
					{!!item.media && (
						<div
							className={mergeClasses(
								classes.startMedia,
								classesName?.startMedia,
								selectedIconClass(item)
							)}
						>
							{item.media}
						</div>
					)}

					{rowAction}

					<Flex>
						{showEndMedia && (
							<div
								className={mergeClasses(
									classes.endMedia,
									classesName?.endMedia
								)}
							>
								{resolvedEndMedia}
							</div>
						)}
					</Flex>
				</li>
			);
		},
		[
			selectedId,
			selectedClass,
			onClickCallback,
			hoveredItemKey,
			hoveredItemLayout,
			getEndMedia,
			rowActionTabIndex,
		]
	);

	const listItems = useMemo(
		() => items.map(listItemBuilder),
		[items, listItemBuilder]
	);

	return (
		<ul
			ref={listRef}
			className={mergeClasses(classes.root, classesName?.containerHeight)}
			style={styles?.container}
		>
			{listItems}
			{props.nextPageSpinner}
		</ul>
	);
};
